import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { buildSectionSelector, morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const itemKey = cartItemRowToRemove.dataset.key;
    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === itemKey),
    ];

    const removeButton = cartItemRowToRemove.querySelector('.cart-items__remove');
    if (removeButton) removeButton.classList.add('is-loading');
    cartItemRowToRemove.classList.add('loading-active');

    // 1. Run animation and server update in parallel for speed
    const animationPromise = Promise.all(rowsToRemove.map((row) => {
      return new Promise((resolve) => {
        if (prefersReducedMotion()) return resolve();
        row.style.setProperty('--row-height', `${row.clientHeight}px`);
        row.classList.add('removing');
        onAnimationEnd(row, resolve);
        setTimeout(resolve, 600); // Safety fallback
      });
    }));

    // Start the server update (with skipMorph: true — we control the morph ourselves)
    const serverPromise = this.updateQuantity({
      id: itemKey,
      quantity: 0,
      action: 'clear',
    }, true).catch((e) => {
      console.warn('Cart removal API error', e);
      return null;
    });

    // Wait for both animation and server
    const [, response] = await Promise.all([animationPromise, serverPromise]);

    if (response?.errors) {
      // Revert visual state on error
      rowsToRemove.forEach((row) => {
        row.classList.remove('removing');
        row.style.removeProperty('--row-height');
      });
      if (removeButton) removeButton.classList.remove('is-loading');
      cartItemRowToRemove.classList.remove('loading-active');
      return;
    }

    // 2. Handle empty cart state
    const isEmptyCart = response?.item_count === 0 || rowsToRemove.length === this.refs.cartItemRows.length;
    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      if (this.isDrawer) {
        // For drawer: sectionRenderer would do a full morph that disrupts the open dialog.
        // Instead: manually hide the summary and mark dialog as empty.
        const drawerDialog = this.closest('dialog');
        if (drawerDialog) {
          drawerDialog.classList.add('cart-drawer--empty');
          const summary = drawerDialog.querySelector('.cart-drawer__summary');
          if (summary) summary.style.display = 'none';
        }
        // Update cart bubble to 0
        this.dispatchEvent(
          new CartUpdateEvent(response, this.sectionId, {
            itemCount: 0,
            source: 'cart-items-component',
            sections: response?.sections ?? {},
            skipMorph: true,
          })
        );
      } else {
        // For cart page: safe to do a fresh section render (no dialog to disrupt)
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }
      return;
    }

    // 3. Remove rows from DOM
    rowsToRemove.forEach((row) => row.remove());

    // 4. Sync summary
    if (response?.sections?.[this.sectionId]) {
      const sectionHTML = response.sections[this.sectionId];

      if (this.isDrawer) {
        // For the drawer: DO NOT morph the full section — it would destroy the open dialog
        // and leave scroll-lock active (causing page to get stuck).
        // Instead, manually update ONLY the price/count elements from the response.
        const fragment = new DOMParser().parseFromString(sectionHTML, 'text/html');

        // Update cart total
        const newTotal = fragment.querySelector('[ref="cartTotal"]');
        if (newTotal) {
          document.querySelectorAll('[ref="cartTotal"]').forEach((el) => {
            el.textContent = newTotal.textContent;
            const val = newTotal.getAttribute('value');
            if (val) el.setAttribute('value', val);
          });
        }

        // Update subtotal (only shown when discounts are active)
        const newSubtotal = fragment.querySelector('[data-testid="cart-subtotal-value"]');
        if (newSubtotal) {
          document.querySelectorAll('[data-testid="cart-subtotal-value"]').forEach((el) => {
            el.innerHTML = newSubtotal.innerHTML;
          });
        }
      } else {
        // For the cart page: 'full' morph is safe — updates the entire section
        // including the summary block with the correct totals.
        morphSection(this.sectionId, sectionHTML, 'full');
      }

      // Update cart bubble count
      const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const newCartItemCount = parseInt(newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent || '0', 10);
      this.dispatchEvent(
        new CartUpdateEvent(response, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: response.sections,
          skipMorph: true,
        })
      );
    } else {
      // Fallback: fresh fetch of the section (guaranteed to include summary)
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    this.#updateQuantitySelectors(response);
    this.#updateCartQuantitySelectorButtonStates();
  }



  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} [config.line] - The line.
   * @param {string} [config.id] - The line item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {boolean} [skipMorph] - Whether to skip morphing.
   * @returns {Promise<any>}
   */
  updateQuantity(config, skipMorph = false) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, id, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const bodyData = {
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    if (id) {
      bodyData.id = id;
    } else {
      bodyData.line = line;
    }

    const body = JSON.stringify(bodyData);

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return parsedResponseText;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
            skipMorph: skipMorph,
          })
        );

        if (!skipMorph) {
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');
        }

        this.#updateCartQuantitySelectorButtonStates();

        return parsedResponseText;
      })
      .catch((error) => {
        console.error(error);
        throw error;
      })
      .finally(() => {
        this.#enableCartItems();

        // Strip any rogue loading classes from elements that inherited them during morphDOM transformations
        this.querySelectorAll('.is-loading').forEach(el => el.classList.remove('is-loading'));
        this.querySelectorAll('.loading-active').forEach(el => el.classList.remove('loading-active'));

        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this || event.detail.data?.skipMorph) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      if (this.isDrawer) {
        this.#morphDrawerFromSection(cartItemsHtml);
      } else {
        morphSection(this.sectionId, cartItemsHtml);
      }

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Updates drawer cart contents without replacing cart-drawer-component (preserves dialog + auto-open).
   * @param {string} sectionHTML
   */
  #morphDrawerFromSection(sectionHTML) {
    const fragment = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const newSection = fragment.getElementById(buildSectionSelector(this.sectionId));

    if (!newSection) {
      morphSection(this.sectionId, sectionHTML, 'hydration');
      return;
    }

    const newCartItems = newSection.querySelector('cart-items-component');
    if (!newCartItems) {
      morphSection(this.sectionId, sectionHTML, 'hydration');
      return;
    }

    const dialog = this.closest('dialog');
    const newDialog = newSection.querySelector('.cart-drawer__dialog');
    const isEmptyCart = Boolean(newDialog?.classList.contains('cart-drawer--empty'));
    // Capture before morph — cart-items morph moves nodes from the parsed tree into the live DOM.
    const newSummary = newDialog?.querySelector('.cart-drawer__summary');

    morph(this, newCartItems, MORPH_OPTIONS);

    if (dialog && newDialog) {
      dialog.classList.toggle('cart-drawer--empty', isEmptyCart);

      const summary = dialog.querySelector('.cart-drawer__summary');

      if (summary && newSummary && summary !== newSummary) {
        morph(summary, newSummary, MORPH_OPTIONS);
      }

      if (summary) {
        summary.style.display = isEmptyCart ? 'none' : '';
      }
    }

    const newTotal = fragment.querySelector('[ref="cartTotal"]');
    if (newTotal) {
      document.querySelectorAll('[ref="cartTotal"]').forEach((el) => {
        el.textContent = newTotal.textContent;
        const val = newTotal.getAttribute('value');
        if (val) el.setAttribute('value', val);
      });
    }

    const newSubtotal = fragment.querySelector('[data-testid="cart-subtotal-value"]');
    if (newSubtotal) {
      document.querySelectorAll('[data-testid="cart-subtotal-value"]').forEach((el) => {
        el.innerHTML = newSubtotal.innerHTML;
      });
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
